import type { ConversionSettings, TargetMimeType } from '../types';
import { convertVideoWithFfmpeg } from './ffmpegClient';

export const MAX_VIDEO_BYTES = 500 * 1024 * 1024;

export const MIME_TO_EXT: Record<TargetMimeType, string> = {
  'video/mp4': 'mp4',
  'video/quicktime': 'mov',
  'video/webm': 'webm',
  'video/x-matroska': 'mkv',
  'video/x-msvideo': 'avi',
  'video/x-m4v': 'm4v',
  'image/gif': 'gif',
};

export const ACCEPTED_MIME = [
  'video/mp4',
  'video/quicktime',
  'video/webm',
  'video/x-matroska',
  'video/x-msvideo',
  'video/x-m4v',
];

export const ACCEPTED_EXT = ['.mp4', '.mov', '.webm', '.mkv', '.avi', '.m4v'];

export const getFileExtension = (filename: string): string => {
  const ext = filename.split('.').pop();
  return ext ? ext.toLowerCase() : '';
};

export const getExtensionFromMime = (mimeType: TargetMimeType): string => MIME_TO_EXT[mimeType] ?? 'mp4';

export const getOutputName = (filename: string, targetFormat: TargetMimeType): string => {
  const dotIndex = filename.lastIndexOf('.');
  const baseName = dotIndex > 0 ? filename.slice(0, dotIndex) : filename;
  return `${baseName}.${getExtensionFromMime(targetFormat)}`;
};

export const isOversizedVideoFile = (file: File): boolean => file.size > MAX_VIDEO_BYTES;

export const isSupportedVideoFile = (file: File): boolean => {
  if (isOversizedVideoFile(file)) return false;
  const ext = `.${getFileExtension(file.name)}`;
  return ACCEPTED_MIME.includes(file.type.toLowerCase()) || ACCEPTED_EXT.includes(ext);
};

export const formatBytes = (bytes: number): string =>
  bytes > 1_048_576
    ? `${(bytes / 1_048_576).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

export const convertVideo = (
  file: File,
  targetFormat: TargetMimeType,
  settings: ConversionSettings,
  onProgress?: (progress: number) => void
): Promise<Blob> => convertVideoWithFfmpeg(file, targetFormat, settings, onProgress);
